import React, { useEffect, useState, useMemo } from "react";
import { Table, Input, Tag, Space, Popconfirm, DatePicker, Button, Modal, Form, InputNumber, Select, Checkbox, message } from "antd";
import { PlusOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
// import CreateLocationForm from "./createLocationForm";
import { DownOutlined, UpOutlined, DownloadOutlined } from "@ant-design/icons";
import SelectProjectsModal from "./selectProjects";
import "./selectProjects/projectSelector.less";
import { ReactComponent as EditIcon } from "../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/delete-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteLocation,
  fetchAllLocations,
  updateLocation,
  createLocation,
  setLocationDetailsIsEdit
} from "./redux/actions";
import SpinnerComponent from "../../components/spinner";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import CustomPagination from "../../components/customPagination/CustomPagination";
import moment from "moment";
import CustomCalendar from "../../components/calendar";
import AMultiSelect from "../../components/AMultiSelect";
import { fetchAllProjects } from '../ProjectSetup/redux/actions'; // Corrected fetchAllProjects import

const LocationsManagerTable = React.forwardRef(({ canUpdate, canDelete }, ref) => {
  const [sortedInfo, setSortedInfo] = useState(null);
  const [statusCheckedList, setStatusCheckedList] = useState([]);
  const projectStatuses = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];
  const [dataSource, setDataSource] = useState([]);
  const [currentDelRecord, setCurrentDelRecord] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(() => 1);
  const [resultPerPage, setResultPerPage] = useState(() => 10);
  const [search, setSearch] = useState({});
  const [defaultPageOneReq, setDefaultPageOneReq] = useState(false);
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  const [date, setDate] = useState(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingKey, setEditingKey] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newLocation, setNewLocation] = useState(null);
  const [form] = Form.useForm();

  const { data: projectsListFromStore, loading: projectsLoadingFromStore, error: projectsError } = useSelector(
    state => state.projectSetup?.projectsList || { data: [], loading: false, error: null } 
  );

  useEffect(() => {
    const projectsData = projectsListFromStore || [];
    if (projectsData.length === 0 && !projectsLoadingFromStore && !projectsError) {
      dispatch(fetchAllProjects({ search: {}, perPage: 0 }));
    }
  }, [dispatch, projectsLoadingFromStore, projectsError]);

  const handleModalClose = () => {
    setIsCreateModalVisible(false);
    setEditingLocation(null);
    dispatch(fetchAllLocations({
      page: 1,
      per_page: resultPerPage,
      search,
      sort: sortedInfo?.columnKey,
      order: sortedInfo?.order,
      show_by_default: true,
    }));
  };

  const isEditing = (record) => record.key === editingKey;
  
  const edit = (record) => {
    form.setFieldsValue({
      name: record.name,
      projects: record.projects || [],
      address: record.address,
      latitude: record.latitude,
      longitude: record.longitude,
      radius: record.radius,
      status: record.status || 'active',
    });
    setEditingKey(record.key);
  };

  // Cancel editing with confirmation
  const cancel = (record) => {
    setEditingKey('');
    if (isAddingNew) {
      setIsAddingNew(false);
      setNewLocation(null);
      setDataSource(dataSource.filter(item => item.key !== 'new-location'));
    }
  };

  // Save changes for existing location
  const saveEdit = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...dataSource];
      const index = newData.findIndex((item) => key === item.id);
      const managedProjectIds = row.projects.length > 0 && typeof row.projects[0] === 'object'
      ? row.projects.map(project => project.id)
      : row.projects;

      if (index > -1) {
        const item = newData[index];
        const updatedItem = {
          ...item,
          ...row,
          managedProjectIds: managedProjectIds, // Map projects to managedProjectIds
        };
        dispatch(updateLocation(updatedItem));
        setEditingKey('');
      } else {
        setEditingKey('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  // Save new location
  const saveNew = async () => {
    try {
      const row = await form.validateFields();
      const payload = {
        ...row,
        managedProjectIds: row.projects, // Map projects to managedProjectIds
      };
      dispatch(createLocation(payload));
      setEditingKey(''); 
      setDataSource(prev => prev.filter(item => item.id !== 'new-location'));
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  // Add new location row
  const addNewLocation = () => {
    const newLoc = {
      key: 'new-location',
      name: '',
      projects: [],
      address: '',
      latitude: null,
      longitude: null,
      radius: null,
      status: 'active',
      isNew: true
    };
    
    setDataSource([newLoc, ...dataSource]);
    setNewLocation(newLoc);
    setIsAddingNew(true);
    setEditingKey('new-location');
    
    form.setFieldsValue({
      name: '',
      projects: [],
      address: '',
      latitude: null,
      longitude: null,
      radius: null,
      status: 'active',
    });
  };

  // Expose methods via ref
  React.useImperativeHandle(ref, () => ({
    showCreateModal: () => setIsCreateModalVisible(true),
    addNewLocation: () => addNewLocation()
  }));

  // Patch: Ensure selector matches root reducer key
  const {
    allLocations,
    fetchLocationsLoading,
    deleteLocationLoading,
    totalRecords,
  } = useSelector((state) => state.locationsReducer || {});
  const { user, projects } = useSelector((state) => state.authReducer || {});

  // Helper function to determine if the search object is effectively empty
  const isSearchEffectivelyEmpty = (currentSearch) => {
    if (Object.keys(currentSearch).length === 0) return true;
    return Object.entries(currentSearch).every(([key, value]) => {
      // Consider a filter effectively empty if its value is null, undefined, an empty string, or an empty array.
      // For the 'status' key, an empty array is specifically treated as 'no filter applied' by the saga.
      if (Array.isArray(value) && value.length === 0) {
        return true; 
      }
      if (value === '' || value === null || value === undefined) {
          return true; 
      }
      return false;
    });
  };

  // Debounced fetch handler
  const debouncedFetch = _.debounce(() => {
    if (resultPerPage > 0) {
      dispatch({ type: 'FETCH_ALL_LOCATIONS_ERROR', data: {} });
      
      const currentPage = defaultPageOneReq ? 1 : page;
      
      if (currentPage > 0) {
        dispatch(
          fetchAllLocations({
            page: currentPage,
            per_page: resultPerPage,
            search, // Uses the current `search` state
            sort: sortedInfo?.columnKey,
            order: sortedInfo?.order,
            show_by_default: isSearchEffectivelyEmpty(search),
          })
        );
      }
    }
    setDefaultPageOneReq(false);
  }, 500);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, [page, resultPerPage, search, sortedInfo?.columnKey, sortedInfo?.order, defaultPageOneReq]);

  // Debounced sort handler
  const debouncedSort = _.debounce((newSortedInfo) => {
    setSortedInfo(newSortedInfo);
  }, 500);

  // Debounced handler for resetting to page one
  const debouncedDefaultPageOne = _.debounce(() => {
    setDefaultPageOneReq(true);
    setTimeout(() => setDefaultPageOneReq(false), 100);
  }, 500);

  useEffect(() => {
    let locationsArray = [];
    if (Array.isArray(allLocations)) {
      locationsArray = allLocations;
    } else if (allLocations && Array.isArray(allLocations.data)) {
      locationsArray = allLocations.data;
    }
    setDataSource(locationsArray.map(loc => ({ ...loc, key: loc.id })));
  }, [allLocations]);

  const handleSelectOnchange = (value, options, key, setHandler) => {
    if (value.includes("all")) {
      setHandler([...options.map((item) => item.value)]);
      value = options.map((item) => item.value);
    } else if (value.includes("not-all")) {
      setHandler([]);
      value = [];
    } else {
      setHandler(value);
    }
    handleMultiSelect(value, key);
  };
  const handleMultiSelect = (list, key) => {
    setSearch({
      ...search,
      [key]: list,
    });
  };

  const handleChange = (pagination, filters, sorter) => {
    if (datePickerOpened) {
      return;
    }
    setSortedInfo(sorter);
  };

  const handleDelete = (record) => {
    setCurrentDelRecord(record);
    dispatch(deleteLocation(record));
  };

  const filterWidths = {
    name: '160px',
    address: '215px',
    projects: '215px',
    latitude: '90px',
    longitude: '90px',
    radius: '90px',
    status: '76px',
  }

  const searchInput = (key) => (
    <Input
      style={{
        height: '25px',
        width: filterWidths[key],
      }}
      inputMode={['longitude', 'latitude', 'radius'].includes(key) ? 'decimal' : undefined}
      pattern={['longitude', 'latitude', 'radius'].includes(key) ? '^-?\\d*\\.?\\d*$' : undefined}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (['longitude', 'latitude', 'radius'].includes(key)) {
          const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
          if (
            allowedKeys.includes(e.key) ||
            (e.ctrlKey || e.metaKey)
          ) {
            return;
          }

          const isNumber = /^[0-9]$/.test(e.key);
          const isDot = e.key === '.' && !e.target.value.includes('.');
          const isMinus = e.key === '-' && e.target.selectionStart === 0 && !e.target.value.includes('-');

          if (!isNumber && !isDot && !isMinus) {
            e.preventDefault();
          }
        }
      }}
      onChange={(e) => {
        const value = e.target.value;

        setSearch((prev) => {
          const newSearch = { ...prev };
          if (value.trim()) {
            newSearch[key] = value.trim();
          } else {
            delete newSearch[key];
          }
          return newSearch;
        });

        debouncedDefaultPageOne();
      }}
    />
  );

  const dateInput = (key) => (
    <DatePicker
      placeholder=""
      className="activities-time-picker prod-due-date"
      getPopupContainer={() =>
        document.getElementsByClassName(
          "ant-layout-content layout-content site-layout-background custom-textarea"
        )[0]
      }
      suffixIcon={null}
      format="D MMM YYYY"
      onChange={(e) => {
        setSearch({
          ...search,
          [key]: e,
        });
        debouncedDefaultPageOne();
      }}
      onOpenChange={(e) => setDatePickerOpened(e)}
    />
  );

  const handleNavigate = (id) =>
    navigate(`/settings/locations/update-location/${id}`);

  const handleDateInput = (value) => {
    setDate(value);
    if (value === null) {
      setSearch({
        ...search,
        start_date: "",
        end_date: "",
      });
    } else {
      setSearch({
        ...search,
        start_date: value[0],
        end_date: value[1],
      });
    }
    debouncedDefaultPageOne();
  };

  // Create editable cell component
  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    let inputNode;
    
    if (inputType === 'number') {
      if (dataIndex === 'latitude') {
        inputNode = <InputNumber placeholder="Latitude" className="location-latitude-input" step="any" parser={(value) => value.replace(/[^\d.-]/g, '')} />;
      } else if (dataIndex === 'longitude') {
        inputNode = <InputNumber placeholder="Longitude" className="location-longitude-input" step="any" parser={(value) => value.replace(/[^\d.-]/g, '')} />;
      } else if (dataIndex === 'radius') {
        inputNode = <InputNumber placeholder="Radius" className="location-radius-input" min={0.5} precision={1} step={0.5} parser={(value) => value.replace(/[^\d.]/g, '')} />;
      } else {
        inputNode = <InputNumber parser={(value) => value.replace(/[^\d.-]/g, '')} />;
      }
    } else if (inputType === 'select' && dataIndex === 'status') {
      inputNode = (
        <Select className="location-status-select">
          <Select.Option value="active">Active</Select.Option>
          <Select.Option value="inactive">Inactive</Select.Option>
        </Select>
      );
    } else if (inputType === 'select' && dataIndex === 'projects') {
      const ProjectDropdownSelector = ({ record, dataIndex, form }) => { 
        const dispatch = useDispatch();
        const { allProjects, fetchProjectsLoading } = useSelector((state) => state.projectsSetupReducer); 
        const projectsList = allProjects || [];
        
        const [dropdownVisible, setDropdownVisible] = useState(false);
        const [selectedProjects, setSelectedProjects] = useState([]);
        const [isCheckAll, setIsCheckAll] = useState(false);
        const [showAll, setShowAll] = useState(false);
        const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
        const triggerRef = React.useRef(null);
        
        // Update dropdown position when scrolling
        useEffect(() => {
          if (dropdownVisible && triggerRef.current) {
            const updatePosition = () => {
              const rect = triggerRef.current.getBoundingClientRect();
              const windowHeight = window.innerHeight;
              const dropdownHeight = 350; // Approximate height of dropdown with all elements
              
              // Check if there's enough space below
              const spaceBelow = windowHeight - rect.bottom;
              const showAbove = spaceBelow < dropdownHeight;
              
              setDropdownPosition({
                top: showAbove ? (rect.top + window.scrollY) : (rect.bottom + window.scrollY),
                left: rect.left + window.scrollX,
                showAbove: showAbove
              });
            };
            
            // Initial position
            updatePosition();
            
            // Update position on scroll and resize
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
            return () => {
              window.removeEventListener('scroll', updatePosition, true);
              window.removeEventListener('resize', updatePosition);
            };
          }
        }, [dropdownVisible]);
        
        const filteredProjects = useMemo(() => {
          if (showAll) {
            return projectsList;
          }
          // If not showAll, include projects that are showByDefault OR are already selected for this record
          const defaultShownProjects = projectsList.filter(p => p.projectStatus && p.projectStatus.showByDefault === true);
          const selectedProjectObjectsInList = projectsList.filter(p => selectedProjects.includes(p.id));
          
          // Combine and remove duplicates
          const combined = [...defaultShownProjects, ...selectedProjectObjectsInList];
          const uniqueCombined = Array.from(new Set(combined.map(p => p.id)))
                                   .map(id => combined.find(p => p.id === id));
          return uniqueCombined;
        }, [projectsList, showAll, selectedProjects]);

        useEffect(() => {
            let newSelectedProjectIds = [];
            if (record.managedProjects && Array.isArray(record.managedProjects)) {
                if (record.managedProjects.length === 0) {
                    newSelectedProjectIds = [];
                } else if (typeof record.managedProjects[0] === 'number' || typeof record.managedProjects[0] === 'string') {
                    newSelectedProjectIds = [...record.managedProjects];
                } else if (typeof record.managedProjects[0] === 'object' && record.managedProjects[0] !== null && 'id' in record.managedProjects[0]) {
                    newSelectedProjectIds = record.managedProjects.map(p => p.id).filter(id => id !== undefined && id !== null);
                } else {
                    console.warn('ProjectDropdownSelector: record.managedProjects has an unexpected format.', record.managedProjects);
                }
            }

            setSelectedProjects(currentSelectedIds => {
                const sortedCurrent = [...currentSelectedIds].sort();
                const sortedNew = [...newSelectedProjectIds].sort();

                if (sortedCurrent.length === sortedNew.length && sortedCurrent.every((val, index) => val === sortedNew[index])) {
                    return currentSelectedIds; 
                }
                return newSelectedProjectIds; 
            });
        }, [JSON.stringify(record.managedProjects)]); 
        
        const handleProjectChange = (projectIds) => {
          form.setFieldsValue({ [dataIndex]: projectIds }); 
        };
        
        const handleCheckboxChange = (e, projectId) => {
          const { checked } = e.target;
          let newSelected = [...selectedProjects];
          
          if (checked) {
            newSelected.push(projectId);
          } else {
            newSelected = newSelected.filter(id => id !== projectId);
          }
          
          setSelectedProjects(newSelected);
          handleProjectChange(newSelected);
        };
        
        const handleSelectAll = (e) => {
          const { checked } = e.target;
          setIsCheckAll(checked);
          
          if (checked) {
            const allIds = filteredProjects.map(p => p.id);
            setSelectedProjects(allIds);
            handleProjectChange(allIds);
          } else {
            setSelectedProjects([]);
            handleProjectChange([]);
          }
        };
        
        const handleShowAll = (e) => {
          setShowAll(e.target.checked);
        };
        
        const handleSave = () => {
          setDropdownVisible(false);
        };
        
        const handleCancel = () => {
          setDropdownVisible(false);
        };
        
        const formatSelectedProjects = () => {
          if (selectedProjects.length === 0) return '';
          
          const selectedProjectObjects = selectedProjects.map(id => {
            const project = projectsList.find(p => p.id === id);
            return project ? project : { id, name: `Project ${id}` };
          });
                    
          if (selectedProjectObjects.length <= 2) {
            // Check all possible property names that might contain the project number or name
            return selectedProjectObjects.map(p => {
              // Try different property combinations to find the project identifier
              const projectId = p.project_number || p.projectNumber || p.id;
              const projectName = p.client_name || p.client || p.name;
              
              // Return a formatted string with both id and name if available
              if (projectId && projectName) {
                return `${projectId} - ${projectName}`;
              } else if (projectId) {
                return `${projectId}`;
              } else if (projectName) {
                return `${projectName}`;
              } else {
                return `Project ${p.id}`;
              }
            }).join(', ');
          } else {
            return `${selectedProjectObjects.length} Projects Selected`;
          }
        };
        
        return (
          <div className="projects-dropdown-selector" style={{ position: 'relative', zIndex: 900 }}>
            <div 
              className="dropdown-trigger" 
              ref={triggerRef}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setDropdownPosition({
                  top: rect.bottom + window.scrollY,
                  left: rect.left + window.scrollX
                });
                setDropdownVisible(!dropdownVisible);
              }}
            >
              {formatSelectedProjects() ? (
                <span className="selected-projects-text">{formatSelectedProjects()}</span>
              ) : (
                <span className="placeholder">Select Projects</span>
              )}
              <DownOutlined style={{ marginLeft: '5px' }} />
            </div>
            
            {dropdownVisible && (
              <div 
                className={`dropdown-content ${dropdownPosition.showAbove ? 'dropdown-content-above' : ''}`}
                style={{
                  position: 'fixed',
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`,
                  zIndex: 1000,
                  maxHeight: '1500px',
                  overflowY: 'auto',
                  minWidth: '250px',
                  transform: dropdownPosition.showAbove ? 'translateY(-100%)' : 'none'
                }}
              >
                <div className="dropdown-header">
                  <div className="checkbox-row">
                    <Checkbox 
                      checked={isCheckAll}
                      onChange={handleSelectAll}
                    /> Select All
                  </div>
                  <div className="checkbox-row">
                    <Checkbox 
                      checked={showAll}
                      onChange={handleShowAll}
                    /> Show All Projects
                  </div>
                </div>
                
                <div className="dropdown-body">
                  {filteredProjects.map(project => (
                    <div key={project.id} className="checkbox-row">
                      <Checkbox 
                        checked={selectedProjects.includes(project.id)}
                        onChange={(e) => handleCheckboxChange(e, project.id)}
                      />
                      {project.projectNumber || ''} - {project.client || 'Unknown'}
                    </div>
                  ))}
                </div>
                
                <div className="dropdown-footer">
                  <Button 
                    size="small" 
                    type="primary" 
                    onClick={handleSave}
                    style={{ marginRight: '8px' }}
                  >
                    Save
                  </Button>
                  <Button 
                    size="small" 
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      };
      
      inputNode = <ProjectDropdownSelector record={record} dataIndex={dataIndex} form={form} />;
    } else {
      if (dataIndex === 'name') {
        inputNode = <Input placeholder="Location Name" className="location-name-input" />;
      } else if (dataIndex === 'address') {
        inputNode = <Input placeholder="Location Address" className="location-address-input" />;
      } else {
        inputNode = <Input />;
      }
    }
    
    return (
      <td {...restProps} style={{ overflow: 'visible' }}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0, padding: 0, overflow: 'visible' }}
            rules={[
              {
                required: dataIndex !== 'projects', 
                message: `Please Input ${title}!`,
              },
              dataIndex === 'radius' ? {
                type: 'number',
                min: 0.5,
                transform: (value) => Number(value),
                message: 'Radius must be an integer and at least 0.5.',
              } : dataIndex === 'latitude' || dataIndex === 'longitude' ? {
                type: 'number',
                transform: (value) => Number(value),
                message: `${dataIndex.charAt(0).toUpperCase() + dataIndex.slice(1)} must be a valid number.`,
              } : undefined,
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Location Name</div>
            <div>{searchInput("name")}</div>
          </div>
        );
      },
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "name" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (name, record) => {
        const editable = isEditing(record);
        return editable ? (
          <EditableCell
            editing={editable}
            dataIndex="name"
            title="Location Name"
            inputType="text"
            record={record}
          />
        ) : (
          <div onClick={() => handleNavigate(record.id)}>{name}</div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Projects</div>
            <div>{searchInput("projects")}</div>
          </div>
        );
      },
      dataIndex: "managedProjects",
      key: "managedProjects",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "managedProjects" && sortedInfo?.order,
      ellipsis: true,
      width: "15%",
      render: (projectsList, record) => {
        const editable = isEditing(record);
        return editable ? (
          <EditableCell
            editing={editable}
            dataIndex="projects"
            title="Projects"
            inputType="select"
            record={record}
          />
        ) : (
          <div 
            style={{
              whiteSpace: "break-spaces",
            }}
            onClick={() => handleNavigate(record.id)}
          >
            {Array.isArray(projectsList) && projectsList.length > 0 
              ? projectsList.map(p => p.projectNumber + ' ' + p.client).join(' | ')
              : '-'}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Address</div>
            <div>{searchInput("address")}</div>
          </div>
        );
      },
      dataIndex: "address",
      key: "address",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "address" && sortedInfo?.order,
      ellipsis: true,
      width: "20%",
      render: (address, record) => {
        const editable = isEditing(record);
        return editable ? (
          <EditableCell
            editing={editable}
            dataIndex="address"
            title="Address"
            inputType="text"
            record={record}
          />
        ) : (
          <div 
            style={{
              whiteSpace: "break-spaces",
            }}
            onClick={() => handleNavigate(record.id)}
          >
            {address && address.length > 100
              ? `${address.substring(0, 100) + " ..."}` 
              : address}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Latitude</div>
            <div>{searchInput("latitude")}</div>
          </div>
        );
      },
      dataIndex: "latitude",
      key: "latitude",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "latitude" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (latitude, record) => {
        const editable = isEditing(record);
        return editable ? (
          <EditableCell
            editing={editable}
            dataIndex="latitude"
            title="Latitude"
            inputType="number"
            record={record}
          />
        ) : (
          <div onClick={() => handleNavigate(record.id)}>{latitude}</div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Longitude</div>
            <div>{searchInput("longitude")}</div>
          </div>
        );
      },
      dataIndex: "longitude",
      key: "longitude",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "longitude" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      render: (longitude, record) => {
        const editable = isEditing(record);
        return editable ? (
          <EditableCell
            editing={editable}
            dataIndex="longitude"
            title="Longitude"
            inputType="number"
            record={record}
          />
        ) : (
          <div onClick={() => handleNavigate(record.id)}>{longitude}</div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">Radius&nbsp;<span style={{textTransform: 'none'}}>(km)</span></div>
            <div>{searchInput("radius")}</div>
          </div>
        );
      },
      dataIndex: "radius",
      key: "radius",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "radius" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      align: "center",
      render: (radius, record) => {
        const editable = isEditing(record);
        return editable ? (
          <EditableCell
            editing={editable}
            dataIndex="radius"
            title="Radius"
            inputType="number"
            record={record}
          />
        ) : (
          <div onClick={() => handleNavigate(record.id)} style={{ textAlign: "center" }}>{radius} km</div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text qr-code-title">QR Code</div>
          </div>
        );
      },
      dataIndex: "qr_code",
      key: "qr_code",
      ellipsis: true,
      width: "10%",
      render: (qr_code, record) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div onClick={() => !isEditing(record) && handleNavigate(record.id)}>
              {qr_code && <img src={qr_code} alt="QR Code" style={{ width: '50px', height: '50px' }} />}
            </div>
            {qr_code && (
              <DownloadOutlined 
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = qr_code;
                  const locationName = record.name || 'location'; 
                  const filename = `${locationName.toLowerCase().replace(/ /g, '_')}_qr_code.png`;
                  link.setAttribute('download', filename);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{ fontSize: '18px', color: '#f25003', cursor: 'pointer' }} 
              />
            )}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ minWidth: "90px" }}>
            <div className="special-table-header-text">Status</div>
            <div style={{ width: "100%" }}>
              <AMultiSelect
                dropdownClassName="status-selector"
                debouncedCallApi={handleStatusFilterApply} // Changed from debouncedFetch
                disabled={false}
                placeholder=""
                selectTags={statusCheckedList}
                onSelectChange={handleStatusSelectionChange} // Changed from generic handleSelectOnchange
                data={projectStatuses}
                filterOption={(input, option) => {
                  return (
                    String(option?.label)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "status",
      key: "status",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "status" && sortedInfo?.order,
      ellipsis: true,
      width: "10%",
      align: "center",
      render: (status, record) => {
        const editable = isEditing(record);
        if (editable) {
          return (
            <EditableCell
              editing={editable}
              dataIndex="status"
              title="Status"
              inputType="select"
              record={record}
            />
          );
        } else {
          return (
            <Tag
              onClick={() => handleNavigate(record.id)}
              color={status === "active" ? "#7cd175" : "#fa7470"} // Set Tag background color
              style={{
                color: "#000", // Set text color to black
                width: "70px", // Updated width
                height: "20px",
                textAlign: "center",
                fontSize: "12px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {status === "active" ? "Active" : "Inactive"}
            </Tag>
          );
        }
      },
    },
    {
      title: null,
      dataIndex: "",
      key: "action",
      width: "8%",
      align: "center",
      render: (_, record) => {
        if (!canUpdate && !canDelete) {
          return;
        }
        const editable = isEditing(record);
        return deleteLocationLoading && record.id === currentDelRecord.id ? (
          <SpinnerComponent fontSize={14} />
        ) : (
          <Space size={15} align="baseline">
            {editable ? (
              <>
                <SaveOutlined
                  className="custom-icon"
                  onClick={() => record.isNew ? saveNew() : saveEdit(record.key)}
                  style={{ fontSize: '18px', color: '#f25003' }}
                />
                <Popconfirm
                  title="Are you sure you want to cancel?"
                  onConfirm={() => cancel(record)}
                  okText="Yes"
                  cancelText="No"
                >
                  <CloseOutlined
                    className="custom-icon"
                    style={{ fontSize: '18px', color: '#f25003' }}
                  />
                </Popconfirm>
              </>
            ) : (
              <>
                {canUpdate && (
                  <EditIcon
                    width={18}
                    height={18}
                    className="custom-icon"
                    onClick={() => {
                      if (editingKey === '') {
                        edit(record);
                      }
                    }}
                  />
                )}
                {canUpdate && (
                  <Popconfirm
                    title="Are you sure you want to delete this Location?"
                    onConfirm={() => handleDelete(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <DeleteIcon width={18} height={18} className="custom-icon" />
                  </Popconfirm>
                )}
              </>
            )}
          </Space>
        );
      },
    },
  ];

  // New handler for status AMultiSelect onSelectChange: only updates statusCheckedList
  const handleStatusSelectionChange = (value) => {
    if (value.includes("all")) {
      setStatusCheckedList(projectStatuses.map((i) => i.value));
    } else if (value.includes("not-all")) {
      setStatusCheckedList([]);
    } else {
      setStatusCheckedList(value);
    }
  };

  // New handler for status AMultiSelect "Done" button: updates main search state
  const handleStatusFilterApply = () => {
    setSearch(prevSearch => ({ ...prevSearch, status: statusCheckedList }));
  };

  return (
    <>
      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          className="special-table location-manager-table"
          columns={columns}
          scroll={{ x: true }}
          onChange={handleChange}
          dataSource={dataSource}
          pagination={false}
          sortUpIcon={<UpOutlined />}
          sortDownIcon={<DownOutlined />}
          loading={fetchLocationsLoading}
          rowClassName={(record) => isEditing(record) ? 'editable-row' : ''}
        />
      </Form>
      <CustomPagination
        page={page}
        setPage={setPage}
        resultPerPage={resultPerPage}
        setResultPerPage={setResultPerPage}
        debouncedCallApi={debouncedFetch}
        totalRecord={totalRecords}
      />
    </>
  );
});

export default LocationsManagerTable;
